import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useEditProjectElementDetailsConfiguration,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectElementDetailsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useRolesRetrieval,
} from "@planzone/view-models";
import {
  Id,
  ProjectElementDetailsConfiguration as Config,
} from "@planzone/dto";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { HorizontalList } from "@common/horizontalList";
import { Form } from "./form";
import { Props } from "./types";
import styles from "./styles.scss";

function canSubmitForm(profile: Config[number]) {
  return profile.roles.length > 0 && profile.columns.length > 0;
}

function ProjectElementDetailsConfiguration({ project, cancel }: Props) {
  const { t } = useTranslation();
  const { roles } = useRolesRetrieval();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { configuration } =
    useRetrieveProjectElementDetailsConfiguration(planzone);
  const {
    isRequestPending: isEditionRequestPending,
    editProjectElementDetailsConfiguration,
  } = useEditProjectElementDetailsConfiguration(planzone);
  const [formData, setFormData] = React.useState(configuration);
  const [profileToAdd, setProfileToAdd] = React.useState<Config[number] | null>(
    null,
  );
  const [currentProfile, setCurrentProfile] = React.useState(0);
  const usedRoles = formData
    .reduce<Id[]>((roles, profile) => [...roles, ...profile.roles], [])
    .filter((role) =>
      !profileToAdd && formData[currentProfile]
        ? !formData[currentProfile].roles.find((r) => r === role)
        : true,
    );
  const remainingRoles = roles.filter(
    (role) =>
      ![
        ...(formData[currentProfile] ? formData[currentProfile].roles : []),
        ...usedRoles,
      ].find((id) => id === role.name || role.uuid === id),
  );
  const canSubmit = canSubmitForm(
    !!profileToAdd
      ? profileToAdd
      : !!formData[currentProfile]
        ? formData[currentProfile]
        : {
            name: "",
            roles: [],
            columns: [],
          },
  );

  useMessage(
    ({ type, data }) => {
      if (type === events.editProjectElementDetailsConfigurationSucceeded) {
        setProfileToAdd(null);
        setCurrentProfile(data.configuration.length - 1);
        cancel();
      }
    },
    { localOnly: true },
  );

  React.useEffect(() => {
    setFormData(configuration);
  }, [configuration]);

  return (
    <Modal
      header={t("projectElementDetailsConfiguration.title")}
      close={cancel}
    >
      <HorizontalList>
        <div className={styles.tabs}>
          {remainingRoles.length > 0 && (
            <p
              className={classNames(styles.tab, {
                [styles.selectedTab]: !!profileToAdd,
              })}
              onClick={() =>
                setProfileToAdd({
                  name: "",
                  roles: remainingRoles.map(({ name }) => name),
                  columns: template.map(({ uuid }) => ({ key: uuid })),
                })
              }
            >
              + {t("projectElementDetailsConfiguration.addProfile")}
            </p>
          )}
          {formData.map(({ name }, index) => (
            <p
              key={index}
              className={classNames(styles.tab, {
                [styles.selectedTab]: index === currentProfile && !profileToAdd,
              })}
              onClick={() => {
                setProfileToAdd(null);
                setCurrentProfile(index);
              }}
            >
              {name}
            </p>
          ))}
        </div>
      </HorizontalList>
      <form
        className={styles.projectElementDetailsConfiguration}
        onSubmit={(e) => {
          e.preventDefault();

          !isEditionRequestPending &&
            editProjectElementDetailsConfiguration({
              planzone,
              configuration,
              nextConfiguration: !profileToAdd
                ? formData
                : [...formData, profileToAdd],
            });
        }}
      >
        {!!formData[currentProfile] && !profileToAdd && (
          <div className={styles.form}>
            <Form
              project={project}
              formData={formData[currentProfile]}
              usedRoles={usedRoles}
              edit={(profile) =>
                setFormData([
                  ...formData.slice(0, currentProfile),
                  profile,
                  ...formData.slice(currentProfile + 1),
                ])
              }
            />
          </div>
        )}
        {!!profileToAdd && (
          <div className={styles.form}>
            <Form
              project={project}
              formData={profileToAdd}
              usedRoles={usedRoles}
              edit={(profile) => setProfileToAdd(profile)}
            />
          </div>
        )}
        <div className={styles.footer}>
          {!profileToAdd && (
            <div>
              <Button
                type="delete"
                status={isEditionRequestPending ? "pending" : "none"}
                isDisabled={isEditionRequestPending}
                press={() => {
                  editProjectElementDetailsConfiguration({
                    planzone,
                    configuration,
                    nextConfiguration: [
                      ...configuration.slice(0, currentProfile),
                      ...configuration.slice(currentProfile + 1),
                    ],
                  });
                  setCurrentProfile(0);
                }}
              >
                {t("projectElementDetailsConfiguration.remove")}
              </Button>
            </div>
          )}
          <div>
            <Button type="cancel" press={cancel}>
              {t("projectElementDetailsConfiguration.cancel")}
            </Button>
            <Button
              type="submit"
              status={isEditionRequestPending ? "pending" : "none"}
              isDisabled={isEditionRequestPending || !canSubmit}
            >
              {!profileToAdd
                ? t("projectElementDetailsConfiguration.edit")
                : t("projectElementDetailsConfiguration.add")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectElementDetailsConfiguration };
